import React, { useState } from 'react';
import { OC, FONT } from '../constants/index.js';
import { Button, Badge } from './ui.js';
import {
  formatWhatsAppSentAt,
  getWhatsAppStagedContext,
  isWhatsAppStagedItem,
  whatsAppRawBody,
} from '../lib/stagedWhatsApp.js';
import OCApi from '../api/client.js';

export function StagedQueueItem({
  item,
  theme,
  t,
  cardStyle,
  selected,
  onToggleSelect,
  onApprove,
  onReject,
  busyId,
}) {
  const [showRaw, setShowRaw] = useState(false);
  const fromWhatsApp = isWhatsAppStagedItem(item);
  const wa = getWhatsAppStagedContext(item);
  const rawBody = whatsAppRawBody(item);

  const metaParts = [
    item.organization,
    item.sourceName,
    item.categoryName,
  ].filter(Boolean);

  return React.createElement(
    'div',
    {
      style: {
        ...cardStyle,
        marginBottom: 12,
        display: 'grid',
        gridTemplateColumns: 'auto 1fr auto',
        gap: 14,
        alignItems: 'start',
        borderLeft: fromWhatsApp ? `3px solid ${OC.success}` : undefined,
      },
    },
    React.createElement('input', {
      type: 'checkbox',
      checked: !!selected,
      onChange: onToggleSelect,
      style: { marginTop: 6 },
    }),
    React.createElement('div', { style: { minWidth: 0 } },
      React.createElement('div', { style: { display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 6 } },
        fromWhatsApp
          ? React.createElement(Badge, { theme, color: '#22C55E' }, 'WhatsApp forward')
          : null,
        item.categoryName
          ? React.createElement(Badge, { theme, color: OC.orange }, item.categoryName)
          : null,
      ),
      React.createElement('div', { style: { fontWeight: 700, fontSize: 16, color: t.text.primary } }, item.title),
      React.createElement('div', { style: { fontSize: 13, color: t.text.secondary, marginTop: 4 } },
        metaParts.join(' · '),
      ),
      item.locationRaw
        ? React.createElement('div', { style: { fontSize: 12.5, color: t.text.secondary, marginTop: 4 } }, item.locationRaw)
        : null,
      item.applyUrl
        ? React.createElement('a', {
            href: item.applyUrl,
            target: '_blank',
            rel: 'noopener noreferrer',
            style: { fontSize: 12.5, color: OC.orange, marginTop: 6, display: 'inline-block', wordBreak: 'break-all' },
          }, item.applyUrl)
        : null,

      fromWhatsApp && wa
        ? React.createElement('div', {
            style: {
              marginTop: 12,
              padding: '10px 12px',
              borderRadius: 10,
              background: theme === 'dark' ? 'rgba(34, 197, 94, 0.08)' : 'rgba(34, 197, 94, 0.06)',
              border: `1px solid ${theme === 'dark' ? 'rgba(34, 197, 94, 0.25)' : 'rgba(34, 197, 94, 0.2)'}`,
              fontSize: 12.5,
              lineHeight: 1.55,
              color: t.text.secondary,
            },
          },
            React.createElement('div', { style: { fontWeight: 600, color: t.text.primary, marginBottom: 6, fontSize: 12 } },
              'WhatsApp context',
            ),
            [
              wa.sourceChatTitle && ['Source group', wa.sourceChatTitle],
              wa.destinationChatTitle && ['Posted to', wa.destinationChatTitle],
              wa.ruleName && ['Forwarding rule', wa.ruleName],
              formatWhatsAppSentAt(wa.sentAt) && ['Sent to channel', formatWhatsAppSentAt(wa.sentAt)],
              wa.platformMessageId && ['Message id', wa.platformMessageId],
            ]
              .filter(Boolean)
              .map(([label, value]) =>
                React.createElement('div', { key: label, style: { marginBottom: 2 } },
                  React.createElement('span', { style: { color: t.text.muted || t.text.secondary } }, `${label}: `),
                  React.createElement('span', { style: { color: t.text.primary } }, value),
                ),
              ),
            wa.mediaUrl
              ? React.createElement('a', {
                  href: wa.mediaUrl,
                  target: '_blank',
                  rel: 'noopener noreferrer',
                  style: { display: 'inline-block', marginTop: 8, color: OC.orange, fontSize: 12 },
                }, 'View attached image')
              : null,
          )
        : null,

      fromWhatsApp && rawBody
        ? React.createElement('div', { style: { marginTop: 10 } },
            React.createElement('button', {
              type: 'button',
              onClick: () => setShowRaw((v) => !v),
              style: {
                border: 'none',
                background: 'transparent',
                color: OC.orange,
                fontSize: 12.5,
                fontWeight: 600,
                cursor: 'pointer',
                padding: 0,
                fontFamily: FONT.body,
              },
            }, showRaw ? 'Hide original WhatsApp text' : 'Show original WhatsApp text'),
            showRaw
              ? React.createElement('pre', {
                  style: {
                    marginTop: 8,
                    padding: 12,
                    borderRadius: 10,
                    background: t.bg.subtle,
                    border: `1px solid ${t.border.base}`,
                    fontSize: 12,
                    lineHeight: 1.5,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    maxHeight: 280,
                    overflow: 'auto',
                    fontFamily: FONT.mono,
                    color: t.text.primary,
                  },
                }, rawBody)
              : null,
          )
        : null,

      !fromWhatsApp && item.detailPayload
        ? React.createElement('div', { style: { fontSize: 12, color: t.text.secondary, marginTop: 8, lineHeight: 1.5 } },
            (OCApi.buildDetailFields(item.categorySlug || 'jobs', null, item.detailPayload) || [])
              .slice(0, 4)
              .map((field) => `${field.label}: ${field.value}`)
              .join(' · '),
          )
        : null,

      item.createdAt
        ? React.createElement('div', { style: { fontSize: 11, color: t.text.muted || t.text.secondary, marginTop: 8, fontFamily: FONT.mono } },
            `Staged ${new Date(item.createdAt).toLocaleString()}`,
          )
        : null,
    ),
    React.createElement('div', { style: { display: 'flex', gap: 8, flexWrap: 'wrap' } },
      React.createElement(Button, {
        kind: 'primary',
        size: 'sm',
        theme,
        onClick: onApprove,
        disabled: busyId === item.id,
      }, 'Approve'),
      React.createElement(Button, {
        kind: 'secondary',
        size: 'sm',
        theme,
        onClick: onReject,
        disabled: busyId === item.id,
      }, 'Reject'),
    ),
  );
}
